import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '@shared/hooks/use-toast';
import type { SelectedProduct } from '@shared/components/ui/ProductSelector';
import { CreateEntryFormData, transformFormDataToAPI } from '../models/CreateEntrySchema';
import { useCreateEntry } from './useCreateEntry';

interface UseSubmitEntryProps {
  /** Instancia del formulario react-hook-form */
  form: UseFormReturn<CreateEntryFormData>;
  /** Productos seleccionados */
  selectedProducts: SelectedProduct[];
  /** Callback para resetear productos seleccionados */
  onResetSelectedProducts: () => void;
}

interface UseSubmitEntryReturn {
  /** Función para enviar el formulario */
  onSubmit: (data: CreateEntryFormData) => void;
  /** Función para cancelar y volver */
  onCancel: () => void;
  /** Estado de carga */
  isLoading: boolean;
  /** Error del envío si existe */
  error: Error | null;
}

/**
 * Custom hook para manejar el envío del formulario de creación de entrada
 */
export const useSubmitEntry = ({
  form,
  selectedProducts,
  onResetSelectedProducts,
}: UseSubmitEntryProps): UseSubmitEntryReturn => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation('merchandiseV3');
  const { mutate: createEntry, isPending: isLoading, error } = useCreateEntry();
  const { reset } = form;

  // Handler para cancelar - resetea todo y navega de vuelta
  const onCancel = useCallback(() => {
    reset();
    onResetSelectedProducts();
    navigate('/merchandise');
  }, [reset, onResetSelectedProducts, navigate]);

  // Handler para enviar el formulario
  const onSubmit = useCallback(
    (data: CreateEntryFormData) => {
      // Validar que hay productos seleccionados
      if (!selectedProducts.length) {
        toast({
          title: t('messages.no_products', 'Sin productos'),
          description: t('messages.no_products_description', 'Debe seleccionar al menos un producto'),
          variant: 'destructive',
        });
        return;
      }

      // Transformar datos del formulario al formato de la API
      const apiData = transformFormDataToAPI(data);

      // Enviar la entrada
      createEntry(apiData, {
        onSuccess: (result) => {
          // Mostrar toast de éxito
          toast({
            title: t('messages.create_success', 'Entrada creada'),
            description: t('messages.create_success_description', 'La entrada se ha creado correctamente'),
            variant: 'default',
          });

          // Resetear formulario y estado
          reset();
          onResetSelectedProducts();

          // Navegar de vuelta a la lista
          navigate('/merchandise');
        },
        onError: (error: any) => {
          // Extraer mensaje de error de la API
          let errorMessage = t('messages.create_error', 'Error al crear la entrada');

          // Prioridad 1: Error con estructura { error: { message: "..." } }
          if (error?.response?.data?.error?.message) {
            errorMessage = error.response.data.error.message;
          }
          // Prioridad 2: Error con estructura { message: "..." }
          else if (error?.response?.data?.message) {
            errorMessage = error.response.data.message;
          }
          // Prioridad 3: Error genérico
          else if (error?.message) {
            errorMessage = error.message;
          }

          // Mostrar toast con el mensaje de error
          toast({
            title: t('messages.create_error', 'Error al crear la entrada'),
            description: errorMessage,
            variant: 'destructive',
          });
        },
      });
    },
    [selectedProducts, createEntry, reset, onResetSelectedProducts, navigate, toast, t],
  );

  return {
    onSubmit,
    onCancel,
    isLoading,
    error,
  };
};
